import React, { useMemo, useState } from 'react';
import type { WorkflowComponent, WorkflowPrompts } from '../../../types/workflow';
import {
  OPTIONAL_PROMPT_KEYS,
  REQUIRED_PROMPT_KEYS,
  collectFlags,
  computedExitTriggers,
  guardLabel,
  type EditableWorkflow,
} from '../../../utils/workflowDsl';
import { ChipsInput, Field, FieldGroup, LinesField, Pill, Section, StructuredField } from './WorkflowFormControls';
import WorkflowAgentsEditor from './WorkflowAgentsEditor';
import { stateAgentsSummary } from '../../../utils/workflowAgents';

export interface WorkflowStateInspectorProps {
  wf: EditableWorkflow;
  stateId: string;
  components: WorkflowComponent[];
  /** `undefined` values remove the key from the state. */
  onPatch: (patch: Record<string, unknown>) => void;
  onRename: (newId: string) => void;
  onDelete: () => void;
  onSetEntry: () => void;
  onSelectEdge: (edgeId: string) => void;
  onSelectState: (stateId: string) => void;
}

const PROMPT_TITLES: Record<string, string> = {
  system_prompt: 'System prompt',
  reminder_prompt: 'Reminder prompt',
  stage_context_prompt: 'Stage context prompt',
  situational_prompt: 'Situational prompt',
  corrective_prompt: 'Corrective prompt',
  alliance_check_prompt: 'Alliance check prompt',
  unit_type_check_prompt: 'Unit type check prompt',
};

const WorkflowStateInspector: React.FC<WorkflowStateInspectorProps> = ({
  wf,
  stateId,
  components,
  onPatch,
  onRename,
  onDelete,
  onSetEntry,
  onSelectEdge,
  onSelectState,
}) => {
  const state = wf.states[stateId];
  const [idDraft, setIdDraft] = useState(stateId);
  const [lastStateId, setLastStateId] = useState(stateId);
  if (lastStateId !== stateId) {
    setLastStateId(stateId);
    setIdDraft(stateId);
  }

  const flagSuggestions = useMemo(() => collectFlags(wf), [wf]);
  const interruptNames = useMemo(() => wf.interrupts.map((it) => it.name).filter(Boolean), [wf.interrupts]);

  const strategyCards = useMemo(() => {
    const byName: Record<string, number[]> = {};
    components
      .filter((c) => c.kind === 'strategy')
      .forEach((c) => {
        if (!byName[c.name]) byName[c.name] = [];
        if (byName[c.name].indexOf(c.version) === -1) byName[c.name].push(c.version);
      });
    Object.keys(byName).forEach((n) => byName[n].sort((a, b) => b - a));
    return byName;
  }, [components]);
  const strategyNames = useMemo(() => Object.keys(strategyCards).sort(), [strategyCards]);

  const outgoing = wf.edges.filter((e) => e.source === stateId);
  const incoming = wf.edges.filter((e) => e.target === stateId);
  const exitTriggers = computedExitTriggers(wf, stateId);

  if (!state) return null;

  const commitRename = () => {
    const next = idDraft.trim();
    if (!next || next === stateId) {
      setIdDraft(stateId);
      return;
    }
    if (wf.states[next]) {
      setIdDraft(stateId);
      return;
    }
    onRename(next);
  };

  const setPrompt = (key: keyof WorkflowPrompts, value: string, required: boolean) => {
    const prompts: WorkflowPrompts = { ...(state.prompts || { system_prompt: '', reminder_prompt: '', stage_context_prompt: '' }) };
    if (!required && !value) delete prompts[key];
    else prompts[key] = value;
    onPatch({ prompts });
  };

  const interruptMode: 'unrestricted' | 'none' | 'only' =
    state.interrupt_triggers == null ? 'unrestricted' : state.interrupt_triggers.length === 0 ? 'none' : 'only';

  const usesStrategyRef = state.strategies_ref != null;
  const strategyRefName = state.strategies_ref?.name || '';
  const strategyVersions = strategyRefName ? strategyCards[strategyRefName] || [] : [];

  return (
    <div className="cwf-inspector">
      <div className="cwf-inspector__head">
        <div className="cwf-inspector__title">
          <i className="fas fa-circle-notch" aria-hidden="true" />
          <span>State</span>
          {wf.entry === stateId ? <Pill tone="ok">entry</Pill> : null}
        </div>
        <div className="cwf-inspector__actions">
          {wf.entry !== stateId ? (
            <button type="button" className="cwf-btn cwf-btn--xs" onClick={onSetEntry} title="Make this the entry state">
              <i className="fas fa-sign-in-alt" aria-hidden="true" /> Set as entry
            </button>
          ) : null}
          <button type="button" className="cwf-btn cwf-btn--xs cwf-btn--danger" onClick={onDelete} title="Delete this state and its transitions">
            <i className="fas fa-trash" aria-hidden="true" />
          </button>
        </div>
      </div>

      <Section title="Identity" defaultOpen summary={state.state_name}>
        <Field label="state_id" hint="Renaming rewrites every transition, interrupt, and the entry pointing at this state.">
          <div className="cwf-inline">
            <input
              className="cwf-input cwf-input--mono"
              value={idDraft}
              onChange={(e) => setIdDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') setIdDraft(stateId);
              }}
              onBlur={commitRename}
            />
          </div>
          {idDraft.trim() && idDraft.trim() !== stateId && wf.states[idDraft.trim()] ? (
            <span className="cwf-field__error">A state with this id already exists.</span>
          ) : null}
        </Field>
        <Field label="state_name">
          <input className="cwf-input" value={state.state_name || ''} onChange={(e) => onPatch({ state_name: e.target.value })} />
        </Field>
        <Field label="primary_goal">
          <textarea className="cwf-textarea" rows={2} value={state.primary_goal || ''} onChange={(e) => onPatch({ primary_goal: e.target.value })} />
        </Field>
        <LinesField label="secondary_goals" value={Array.isArray(state.secondary_goals) ? state.secondary_goals : []} onChange={(next) => onPatch({ secondary_goals: next })} />
        <div className="cwf-grid-3">
          <Field label="stage" hint="1–4 or special">
            <input
              className="cwf-input"
              list="cwf-stage-options"
              value={state.stage == null ? '' : String(state.stage)}
              onChange={(e) => {
                const raw = e.target.value.trim();
                if (!raw) onPatch({ stage: undefined });
                else if (/^\d+$/.test(raw)) onPatch({ stage: Number(raw) });
                else onPatch({ stage: raw });
              }}
            />
            <datalist id="cwf-stage-options">
              {['1', '2', '3', '4', 'special'].map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </Field>
          <Field label="phase_within_stage">
            <input
              className="cwf-input"
              value={state.phase_within_stage ?? ''}
              onChange={(e) => onPatch({ phase_within_stage: e.target.value || undefined })}
            />
          </Field>
          <Field label="state_type">
            <input className="cwf-input" value={state.state_type ?? ''} onChange={(e) => onPatch({ state_type: e.target.value || undefined })} />
          </Field>
        </div>
      </Section>

      <Section
        title="Prompts"
        summary={`${REQUIRED_PROMPT_KEYS.filter((k) => (state.prompts?.[k] || '').trim()).length}/3 required filled`}
      >
        {REQUIRED_PROMPT_KEYS.map((key) => (
          <Field key={String(key)} label={<>{PROMPT_TITLES[String(key)]} <Pill tone="warn">required</Pill></>}>
            <textarea
              className="cwf-textarea"
              rows={4}
              value={state.prompts?.[key] || ''}
              onChange={(e) => setPrompt(key, e.target.value, true)}
            />
          </Field>
        ))}
        {OPTIONAL_PROMPT_KEYS.map((key) => (
          <Field key={String(key)} label={PROMPT_TITLES[String(key)]} hint="Optional — an empty prompt is removed from the state.">
            <textarea
              className="cwf-textarea"
              rows={3}
              value={state.prompts?.[key] || ''}
              onChange={(e) => setPrompt(key, e.target.value, false)}
            />
          </Field>
        ))}
      </Section>

      <Section
        title="Strategies"
        summary={usesStrategyRef ? `card: ${strategyRefName}` : state.strategies != null ? 'inline' : 'none'}
      >
        <label className="cwf-check">
          <input
            type="checkbox"
            checked={usesStrategyRef}
            onChange={(e) => {
              if (e.target.checked) onPatch({ strategies_ref: { name: strategyNames[0] || '' } });
              else onPatch({ strategies_ref: undefined });
            }}
          />
          <span>Reference a strategy card from the library</span>
        </label>
        {usesStrategyRef ? (
          <div className="cwf-grid-2">
            <Field label="strategies_ref.name" hint={strategyNames.length ? undefined : 'Library has no strategy cards — type the name manually.'}>
              <input
                className="cwf-input cwf-input--mono"
                list="cwf-strategy-names"
                value={strategyRefName}
                onChange={(e) => onPatch({ strategies_ref: { name: e.target.value } })}
              />
              <datalist id="cwf-strategy-names">
                {strategyNames.map((n) => (
                  <option key={n} value={n} />
                ))}
              </datalist>
            </Field>
            <Field label="version" hint="Without a version the latest card is taken when the graph is applied.">
              <select
                className="cwf-input"
                value={state.strategies_ref?.version == null ? '' : String(state.strategies_ref.version)}
                onChange={(e) => {
                  const v = e.target.value;
                  onPatch({ strategies_ref: { name: strategyRefName, ...(v ? { version: Number(v) } : {}) } });
                }}
              >
                <option value="">latest</option>
                {strategyVersions.map((v) => (
                  <option key={v} value={v}>
                    v{v}
                  </option>
                ))}
                {state.strategies_ref?.version != null && strategyVersions.indexOf(state.strategies_ref.version) === -1 ? (
                  <option value={state.strategies_ref.version}>v{state.strategies_ref.version}</option>
                ) : null}
              </select>
            </Field>
            {state.strategies != null ? (
              <p className="cwf-note">
                The server also returned the expanded <code>strategies</code> block; on save only the reference is sent.
              </p>
            ) : null}
          </div>
        ) : (
          <StructuredField label="strategies (inline)" value={state.strategies} onChange={(next) => onPatch({ strategies: next })} />
        )}
      </Section>

      <Section
        title="Interrupts allowed here"
        summary={interruptMode === 'unrestricted' ? 'not restricted' : interruptMode === 'none' ? 'none' : `${state.interrupt_triggers!.length} selected`}
      >
        <div className="cwf-radios">
          <label className="cwf-check">
            <input type="radio" checked={interruptMode === 'unrestricted'} onChange={() => onPatch({ interrupt_triggers: undefined })} />
            <span>Not restricted (field absent) — any workflow interrupt may fire</span>
          </label>
          <label className="cwf-check">
            <input type="radio" checked={interruptMode === 'none'} onChange={() => onPatch({ interrupt_triggers: [] })} />
            <span>None (<code>[]</code>) — nothing interrupts this state</span>
          </label>
          <label className="cwf-check">
            <input
              type="radio"
              checked={interruptMode === 'only'}
              onChange={() => onPatch({ interrupt_triggers: state.interrupt_triggers && state.interrupt_triggers.length ? state.interrupt_triggers : [interruptNames[0] || ''] })}
            />
            <span>Only the selected interrupts</span>
          </label>
        </div>
        {interruptMode === 'only' ? (
          <ChipsInput
            value={state.interrupt_triggers || []}
            onChange={(next) => onPatch({ interrupt_triggers: next })}
            suggestions={interruptNames}
            placeholder="interrupt name"
          />
        ) : null}
        <p className="cwf-note">A crisis interrupt fires regardless of this setting.</p>
      </Section>

      <Section
        title="Coordinator flags"
        summary={`${(state.coordinator_flags_required || []).length} required · ${(state.coordinator_flags_set || []).length} set`}
      >
        <FieldGroup label="coordinator_flags_required" hint="Flags that must be present before the coordinator may act here.">
          <ChipsInput
            value={state.coordinator_flags_required || []}
            onChange={(next) => onPatch({ coordinator_flags_required: next })}
            suggestions={flagSuggestions}
            placeholder="flag"
          />
        </FieldGroup>
        <FieldGroup label="coordinator_flags_set" hint="Flags raised when this state is entered.">
          <ChipsInput
            value={state.coordinator_flags_set || []}
            onChange={(next) => onPatch({ coordinator_flags_set: next })}
            suggestions={flagSuggestions}
            placeholder="flag"
          />
        </FieldGroup>
      </Section>

      <Section title="Lifecycle" summary={state.exit_conditions ? 'configured' : 'default'}>
        <StructuredField
          label="exit_conditions"
          value={state.exit_conditions}
          onChange={(next) => onPatch({ exit_conditions: next })}
          hint={
            <>
              Free-form object: <code>max_turns</code>, <code>on_max_turns_goto</code>, <code>reminder_every_n_turns</code>, …
            </>
          }
        />
        <div className="cwf-field">
          <span className="cwf-field__label">exit_triggers (computed by the backend from outgoing transitions)</span>
          {exitTriggers.length ? (
            <div className="cwf-chips">
              {exitTriggers.map((t) => (
                <span key={t.name} className="cwf-chip cwf-chip--muted">
                  {t.name} ≥ {t.min_confidence}
                </span>
              ))}
            </div>
          ) : (
            <span className="cwf-note">No trigger-guarded outgoing transitions.</span>
          )}
        </div>
      </Section>

      <Section title="Agents" defaultOpen summary={stateAgentsSummary(state.agents)}>
        <WorkflowAgentsEditor value={state.agents} onChange={(next) => onPatch({ agents: next })} />
        <StructuredField
          label="agents_ref"
          value={state.agents_ref}
          onChange={(next) => onPatch({ agents_ref: next })}
          hint="Reference a shared agents config card from the library instead of the inline agents above."
        />
      </Section>

      <Section title="Advanced">
        <StructuredField label="success_criteria" value={state.success_criteria} onChange={(next) => onPatch({ success_criteria: next })} />
        <StructuredField label="format_requirements" value={state.format_requirements} onChange={(next) => onPatch({ format_requirements: next })} />
        <StructuredField label="map_config" value={state.map_config} onChange={(next) => onPatch({ map_config: next })} />
        <StructuredField label="thresholds" value={state.thresholds} onChange={(next) => onPatch({ thresholds: next })} />
      </Section>

      <Section title="Transitions" defaultOpen summary={`${outgoing.length} out · ${incoming.length} in`}>
        <div className="cwf-field__label">Outgoing</div>
        {outgoing.length ? (
          <ul className="cwf-list">
            {outgoing.map((e) => (
              <li key={e.id}>
                <button type="button" className="cwf-link" onClick={() => onSelectEdge(e.id)}>
                  → <code>{e.target}</code> <span className="cwf-muted">{guardLabel(e.guard) || e.type}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="cwf-note">None — drag from the right or bottom handle of the node to add one.</p>
        )}
        <div className="cwf-field__label">Incoming</div>
        {incoming.length ? (
          <ul className="cwf-list">
            {incoming.map((e) => (
              <li key={e.id}>
                <button type="button" className="cwf-link" onClick={() => onSelectState(e.source)}>
                  <code>{e.source}</code> →
                </button>{' '}
                <button type="button" className="cwf-link cwf-muted" onClick={() => onSelectEdge(e.id)}>
                  {guardLabel(e.guard) || e.type}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="cwf-note">{wf.entry === stateId ? 'Entry state.' : 'No incoming transitions — reachable only via an interrupt.'}</p>
        )}
      </Section>
    </div>
  );
};

export default WorkflowStateInspector;
